import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useLanguageStore } from '../../store/languageStore';
import translations from '../../translations';
import { getExpensesMy } from '../../utils/api';
import { getOfflineExpenses, saveExpenses } from '../../utils/offlineDb';
import { ExpensesResponse, Expense } from '../../types/expenses';
import { format, parseISO } from 'date-fns';
import {
    DollarSign,
    RefreshCw,
    Plus,
    ChevronLeft,
    ChevronRight,
    FileText,
} from 'lucide-react';
import { LoadingSkeleton } from '../common/LoadingSkeleton';
import { ErrorState } from '../common/ErrorState';
import { EmptyState } from '../common/EmptyState';
import { ExpenseCard } from './ExpenseCard';
import { AddExpenseForm } from './AddExpenseForm';

const ITEMS_PER_PAGE = 10;

interface ExpensesByDate {
    [key: string]: Expense[];
}

function formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount);
}

function calculateWorkerStats(expenses: Expense[]) {
    return expenses.reduce(
        (acc, expense) => {
            const amount = expense.amount;
            return {
                total: acc.total + amount,
                spendings:
                    expense.action === 'SPENDING'
                        ? acc.spendings + amount
                        : acc.spendings,
                penalties:
                    expense.action === 'PENALTY'
                        ? acc.penalties + amount
                        : acc.penalties,
            };
        },
        { total: 0, spendings: 0, penalties: 0 },
    );
}

function DateGroup({ date, expenses }: { date: string; expenses: Expense[] }) {
    return (
        <div className='space-y-3'>
            <h3 className='text-sm font-medium text-gray-500 dark:text-gray-400 sticky top-0 bg-light dark:bg-dark py-2'>
                {format(parseISO(date), 'MMMM d, yyyy')}
            </h3>
            <div className='space-y-3'>
                {expenses.map((expense) => (
                    <ExpenseCard key={expense.id} expense={expense} />
                ))}
            </div>
        </div>
    );
}

export function ExpensesView() {
    const navigate = useNavigate();
    const { accessToken, workerId, isAuthenticated } = useAuthStore();
    const { language } = useLanguageStore();
    const t = translations[language];
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<ExpensesResponse | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
        }
    }, [isAuthenticated, navigate]);

    const fetchExpenses = async () => {
        if (!accessToken) {
            setError('Please log in to view expenses');
            navigate('/login');
            return;
        }

        setLoading(true);
        try {
            if (!navigator.onLine) {
                // OFFLINE
                const offlineExpenses = await getOfflineExpenses();

                // теперь workerId в expense может отсутствовать -> НЕ фильтруем
                const mappedExpenses = offlineExpenses.map((expense) => ({
                    ...expense,
                    categoryName:
                        expense.category?.name || expense.categoryName,
                    woNumber: expense.order?.wo_number || expense.woNumber,
                    files: expense.files || [],
                }));

                // stats офлайн считаем локально (потому что в offlineDb stats обычно не хранится)
                const stats = calculateWorkerStats(mappedExpenses);

                const filteredData: ExpensesResponse = {
                    message: 'Offline expenses',
                    meta: {
                        currentPage: 1,
                        limit: ITEMS_PER_PAGE,
                        total_pages: Math.ceil(
                            mappedExpenses.length / ITEMS_PER_PAGE,
                        ),
                        totalExpenses: mappedExpenses.length,
                    },
                    stats,
                    expenses: mappedExpenses,
                };

                setData(filteredData);
            } else {
                // ONLINE
                const response = await getExpensesMy(accessToken);

                // сохраняем в офлайн
                await saveExpenses(response.expenses);

                const mappedExpenses = response.expenses.map((expense) => ({
                    ...expense,
                    categoryName:
                        expense.category?.name || expense.categoryName,
                    woNumber: expense.order?.wo_number || expense.woNumber,
                    files: expense.files || [],
                }));

                const filteredData: ExpensesResponse = {
                    message: response.message,
                    meta: {
                        currentPage: 1,
                        limit: ITEMS_PER_PAGE,
                        total_pages: Math.ceil(
                            mappedExpenses.length / ITEMS_PER_PAGE,
                        ),
                        totalExpenses: mappedExpenses.length,
                    },
                    // ВАЖНО: используем stats с бэкенда
                    stats: response.stats,
                    expenses: mappedExpenses,
                };

                setData(filteredData);
            }

            setError(null);
        } catch (error: any) {
            setError(error.message || 'Failed to fetch expenses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accessToken && workerId) {
            fetchExpenses();
        }
    }, [accessToken, workerId]);

    if (!isAuthenticated) {
        return null;
    }

    if (loading) {
        return (
            <div className='max-w-4xl mx-auto p-4'>
                <LoadingSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className='max-w-4xl mx-auto p-4'>
                <ErrorState message={error} onRetry={fetchExpenses} />
            </div>
        );
    }

    if (!data || data.expenses.length === 0) {
        return (
            <div className='max-w-4xl mx-auto p-4'>
                <div className='flex justify-end mb-6'>
                    <button
                        onClick={() => setShowAddForm(true)}
                        className='btn-primary'
                    >
                        <Plus className='h-4 w-4' />
                        {t.expenses.addExpense}
                    </button>
                </div>
                <EmptyState message={t.expenses.noExpenses} />
                {showAddForm && (
                    <AddExpenseForm
                        onSuccess={fetchExpenses}
                        onClose={() => setShowAddForm(false)}
                    />
                )}
            </div>
        );
    }

    const groupedExpenses = data.expenses.reduce(
        (acc: ExpensesByDate, expense) => {
            const date = format(
                parseISO(expense.date || expense.createdAt),
                'yyyy-MM-dd',
            );
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(expense);
            return acc;
        },
        {},
    );

    const sortedDates = Object.keys(groupedExpenses).sort((a, b) =>
        b.localeCompare(a),
    );
    const totalPages = Math.ceil(sortedDates.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentDates = sortedDates.slice(startIndex, endIndex);

    return (
        <div className='max-w-4xl mx-auto p-4'>
            {isOffline && (
                <div className='mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg flex items-center gap-2'>
                    <span className='text-sm'>{t.expenses.status.offline}</span>
                </div>
            )}
            <div className='flex justify-end mb-6'>
                <div className='flex items-center gap-2'>
                    <button
                        onClick={() => setShowAddForm(true)}
                        className='btn-primary'
                    >
                        <Plus className='h-4 w-4' />
                        {t.expenses.addExpense}
                    </button>
                    <button
                        onClick={fetchExpenses}
                        disabled={loading}
                        className='p-2 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors disabled:opacity-50'
                        title='Refresh expenses'
                    >
                        <RefreshCw
                            className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`}
                        />
                    </button>
                </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
                <div className='bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-4 shadow-soft dark:shadow-soft-dark'>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                        {t.expenses.total}
                    </p>
                    <p className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                        {formatAmount(data.stats.total)}
                    </p>
                </div>
                <div className='bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-4 shadow-soft dark:shadow-soft-dark'>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                        {t.expenses.spendings}
                    </p>
                    <p className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                        {formatAmount(data.stats.spendings)}
                    </p>
                </div>
                <div className='bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-4 shadow-soft dark:shadow-soft-dark'>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                        {t.expenses.penalties}
                    </p>
                    <p className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                        {formatAmount(data.stats.penalties)}
                    </p>
                </div>
            </div>

            <div className='space-y-6'>
                {currentDates.map((date) => (
                    <DateGroup
                        key={date}
                        date={date}
                        expenses={groupedExpenses[date].sort(
                            (a, b) =>
                                new Date(b.date || b.createdAt).getTime() -
                                new Date(a.date || a.createdAt).getTime(),
                        )}
                    />
                ))}
            </div>

            {totalPages > 1 && (
                <div className='flex justify-center items-center gap-4 mt-8'>
                    <button
                        onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                        className='btn-secondary'
                    >
                        <ChevronLeft className='h-4 w-4' />
                        Previous
                    </button>
                    <span className='text-sm text-gray-600 dark:text-gray-400'>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() =>
                            setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages),
                            )
                        }
                        disabled={currentPage === totalPages}
                        className='btn-secondary'
                    >
                        Next
                        <ChevronRight className='h-4 w-4' />
                    </button>
                </div>
            )}

            {showAddForm && (
                <AddExpenseForm
                    onSuccess={fetchExpenses}
                    onClose={() => setShowAddForm(false)}
                />
            )}
        </div>
    );
}
